/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, take, map, switchMap } from 'rxjs/operators';
// import { subjectSubscribeWithoutComplete } from '../plain-javascript/services/rxjs-internal-extensions';
import { DynamicFlagTypes } from './DynamicFlagTypes';
import { fancyFirebaseGetters, masterFancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { FancyDatabase, IRefLike, ISnapshotLike } from '../../common/lib';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FeatureSetting = any; // use any instead of boolean | number | string, to reduce refactoring effort

export enum FeatureFlagType {
	BOOLEAN = 'boolean',
	MAP = 'map',
}

/*
 * This exists mainly to have a set of default flags before data is retrieved from firebase (i.e. angular services)
 * Try to keep the same set of flags both here and in firebase
 */
const DEFAULT_FLAG: Map<string, FeatureSetting> = new Map<string, FeatureSetting>();
DEFAULT_FLAG.set(DynamicFlagTypes.CREW_SIZE_ENABLED, true);
DEFAULT_FLAG.set(DynamicFlagTypes.SHARED_MEETING, true);
DEFAULT_FLAG.set(DynamicFlagTypes.CLIPBOARD, true);
DEFAULT_FLAG.set(DynamicFlagTypes.DASHBOARD, true);
DEFAULT_FLAG.set(DynamicFlagTypes.DASHBOARD_PROJECT_LEVEL_ENABLED, true);
DEFAULT_FLAG.set(DynamicFlagTypes.UNDO, true);
DEFAULT_FLAG.set(DynamicFlagTypes.ANALYTICS_SEGMENT_EVENTS, true);
DEFAULT_FLAG.set(DynamicFlagTypes.SIGN_UP_ENABLED, true);
DEFAULT_FLAG.set(DynamicFlagTypes.WEB_GL_ENABLED, false); // default to false
DEFAULT_FLAG.set(DynamicFlagTypes.CUSTOM_FIELDS_MAX_NUM, 100);
DEFAULT_FLAG.set(DynamicFlagTypes.CUSTOM_FIELDS_MATERIAL_GROUP_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.REFRESH_FOR_NEW_VERSION_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.PROJECT_SCHEDULE_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.PROJECT_PARTITIONING_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.PROJECT_TIME_ZONE_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.WIDE_PULL_TICKET, true);
DEFAULT_FLAG.set(DynamicFlagTypes.FILE_ATTACH_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.ORGANIZATION_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.TICKET_IMPORT_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.REPORT_LOGO_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.ENABLE_GCS_TICKET_ATTACHMENTS_PATH_SEGMENT, false);
DEFAULT_FLAG.set(DynamicFlagTypes.TICKET_DEPENDENCY_LINKAGE_TYPE_AND_LAG, false);
DEFAULT_FLAG.set(DynamicFlagTypes.TICKET_EXPORT_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.CSV_REPORT_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.MARK_PROJECTS_AS_ARCHIVED_IN_DESKTOP, true);
DEFAULT_FLAG.set(DynamicFlagTypes.MASTER_SCHEDULE_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.DOCKED_TICKET_IN_DESKTOP_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.MODERN_TIMELINE_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.BOARDSCAN_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.PLAN_PRINT_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.FBDB_GTP_ENABLED, false);
DEFAULT_FLAG.set(DynamicFlagTypes.FBDB_GTP_ENABLED_BY_PROJECT, false);

// Used by Analytics Decorators
DEFAULT_FLAG.set(DynamicFlagTypes.ANALYTICS_CONSOLE_LOGGING, false);

// file attach stuff
// jpeg webp png tiff are supported by cloud extension, heic heif are converted on the fly
DEFAULT_FLAG.set(DynamicFlagTypes.FILE_ATTACH_SUFFIXES, 'jpeg webp png tiff heic heif');
DEFAULT_FLAG.set(DynamicFlagTypes.FILE_ATTACH_SIZE_LIMIT_IN_MB, 50);

// The following list is based on the "6.0 Add One or More Images" BDD;
// see https://mocasystems.atlassian.net/wiki/spaces/FEAT/pages/1714127277/Desktop+Image+Attachment#6.0-Add-One-or-More-Images
//
// The file upload component needs this suffix list as an array, and wants each suffix prefixed with a '.'/period.
// See TicketEditService.fileAttachEnabled$(), which provides that:
//
//     TicketEditService.fileAttachEnabled$() => [".jpg", ".png", ".heif", ".heic", ".webp"]
DEFAULT_FLAG.set(DynamicFlagTypes.FILE_ATTACH_SUFFIXES, 'jpg png heif heic webp');

// ngMobile ticket-list defaults:
DEFAULT_FLAG.set(DynamicFlagTypes.TICKET_LIST_LOAD_BEFORE_TODAY, 1500);
DEFAULT_FLAG.set(DynamicFlagTypes.TICKET_LIST_LOAD_AFTER_TODAY, 1500);

// Plan Actions
DEFAULT_FLAG.set(DynamicFlagTypes.PLAN_ACTIONS_HOVER_OVER, false);

// Swimlanes Reports
DEFAULT_FLAG.set(DynamicFlagTypes.SWIMLANES_REPORT_ENABLED, false);

// Custom Fields Multi-Select on Reports
DEFAULT_FLAG.set(DynamicFlagTypes.CUSTOM_FIELDS_REPORT_ENABLED, false);

// WBS Code on Tickets and Reports
DEFAULT_FLAG.set(DynamicFlagTypes.TICKETS_WBS_ENABLED, false);

// Project Settings (Data Source Editor)
DEFAULT_FLAG.set(DynamicFlagTypes.PROJECT_SETTINGS_ENABLED, false);

// Touchplan format data sources in Import Tickets
DEFAULT_FLAG.set(DynamicFlagTypes.TOUCHPLAN_FORMAT_ENABLED, false);

// External Linkage Report filter
DEFAULT_FLAG.set(DynamicFlagTypes.EXTERNAL_LINKAGE_REPORT_FILTER_ENABLED, false);

// WBS Code filter in Reports
DEFAULT_FLAG.set(DynamicFlagTypes.WBS_CODE_FILTER_ENABLED, false);

// Idle timeout to logout the user default value
DEFAULT_FLAG.set(DynamicFlagTypes.IDLE_TIMEOUT_ENABLED, true);

// Plan Archive
DEFAULT_FLAG.set(DynamicFlagTypes.PLAN_ARCHIVE_ENABLED, false);

// Additional links in support dropdown
DEFAULT_FLAG.set(DynamicFlagTypes.SUPPORT_DROPDOWN_ADDITIONAL_RESOURCE_LINKS_ENABLED, true);

/**
 * FeatureFlagManager retrieves the flags from firebase, also updates/stores the values in LocalStorage
 */
export class FeatureFlagObserver {
	public featureFlagReady$: Observable<FeatureSetting>;
	private _featureFlagReadySubject$: BehaviorSubject<FeatureSetting>;
	private _flagMap$: Map<string, BehaviorSubject<FeatureSetting> | Map<string, FeatureSetting>>;
	private _shutdownSubject$: Subject<FeatureSetting>;
	private _resetSubjects$: Map<string, BehaviorSubject<FeatureFlagType>> = new Map<
		string,
		BehaviorSubject<FeatureFlagType>
	>();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private _fbdbSettings: Observable<ISnapshotLike<any>> | null = null;

	private readonly _FEATURE_FLAG_LOCAL_STORAGE_PREFIX: string = 'featureFlag_';

	public constructor() {
		this._featureFlagReadySubject$ = new BehaviorSubject<FeatureSetting>(false); // flag to signal settings object has been loaded
		this.featureFlagReady$ = this._featureFlagReadySubject$.pipe(filter((ready) => !!ready)); //only emit if true (suppress initial value)

		this._flagMap$ = new Map<
			string,
			BehaviorSubject<FeatureSetting> | Map<string, BehaviorSubject<FeatureSetting>>
		>();
		this._shutdownSubject$ = new Subject();

		this._populateDefault(DynamicFlagTypes);
	}

	public start<T extends IRefLike>(fancyDatabase: FancyDatabase<T>): void {
		const settingsPath = fancyFirebaseGetters.getSettingsPath();

		const fancyRef = masterFancyFirebaseReferencer.getRef(settingsPath);

		this._fbdbSettings = fancyDatabase.object$(fancyRef);

		this._fbdbSettings
			.pipe(distinctUntilChanged(), takeUntil(this._shutdownSubject$))
			.subscribe((settings) => {
				for (const flag of Object.entries(settings.val())) {
					this._updateMap(flag[0], flag[1]);
				}
			});

		this._fbdbSettings
			.pipe(
				map(() => true),
				take(1)
			)
			.subscribe(this._featureFlagReadySubject$);
	}

	/**
	 * This is preferred way to get feature flag, since it returns BehaviorSubject and change is reactive
	 *
	 * Use case: if a flag has below structure on FB under settings
	 * projects_id
	 * ├── plan_id_1
	 * │	├── user_id_1
	 * │	└── user_id_2
	 * └── plan_id_2
	 *
	 * use getFlag("project_id", "plan_id_1", "user_id_1") to retrieve the flag for user_id_1
	 * same use for getFlag()
	 *
	 * @param args level of data structure to access the flag
	 */
	public getFlag$(...args: string[]): Observable<FeatureSetting> {
		const key = args[0];
		if (!this._resetSubjects$.has(key)) {
			const featureFlagType =
				this._flagMap$.get(key) instanceof BehaviorSubject ? FeatureFlagType.BOOLEAN : FeatureFlagType.MAP;
			this._resetSubjects$.set(key, new BehaviorSubject<FeatureFlagType>(featureFlagType));
		}
		return this._resetSubjects$.get(key)!.pipe(
			distinctUntilChanged(),
			switchMap(() => {
				return this._attemptToRetrieveFlag(...args).asObservable();
			})
		);
	}

	/**
	 * Avoid this synchronous getter if possible, or use it with extreme caution
	 * This could be invoked before data is returned from firebase
	 */
	public getFlag(...args: string[]): FeatureSetting {
		return this._attemptToRetrieveFlag(...args).getValue();
	}

	public destroy(): void {
		this._featureFlagReadySubject$.complete();
		this._destroyObs(this._flagMap$);
		this._shutdownSubject$.next(undefined);
		this._shutdownSubject$.complete();
		this._fbdbSettings = null;
	}

	// Meant to be overwritten if we want to use LocalStorage
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	protected writeLocally(key: string, data: any): void {}

	protected readLocally(key: string): void {}

	private _updateMap(
		key: string,
		value: FeatureSetting | Map<string, FeatureSetting>,
		useMap?: Map<string, FeatureSetting>,
		parentKey?: string
	): void {
		const currentMap = useMap ? useMap : this._flagMap$;
		const valueType = typeof value;
		if (valueType === 'boolean' || valueType === 'number' || valueType === 'string') {
			// in case the flag exists only in firebase, but not in the map
			// or if the value in firebase is different from the value in the map (localstorage)
			// Basically, if the code reaches this point, the map should hold a BehaviorSubject for the flag, if it is something else, it is out of sync (wrong)
			if (!currentMap.has(key) || !(currentMap.get(key) instanceof BehaviorSubject)) {
				currentMap.set(key, new BehaviorSubject<FeatureSetting>(value));
				const resetSubjectKey = parentKey ? parentKey : key;
				if (this._resetSubjects$.has(resetSubjectKey)) {
					this._resetSubjects$.get(resetSubjectKey)!.next(FeatureFlagType.BOOLEAN);
				}
			} else if (currentMap.get(key).getValue() !== value) {
				(currentMap.get(key) as BehaviorSubject<FeatureSetting>).next(value);
			}
			// write to local storage
			this.writeLocally(`${this._FEATURE_FLAG_LOCAL_STORAGE_PREFIX}${key}`, value);
		} else if (typeof value === 'object') {
			// This checks if the value exists in firebase and is a map
			// If the value does not exist or is not a map, it creates a new map
			// Especially in the second case, it means that value of the flag in firebase is out of sync with the value in the map (localstorage)
			// Basically, if the code reaches this point, the map should hold a Map for the flag, if it is something else, it is out of sync (wrong)
			if (!currentMap.has(key) || !(currentMap.get(key) instanceof Map)) {
				currentMap.set(key, new Map<string, BehaviorSubject<FeatureSetting>>());
				if (this._resetSubjects$.has(key)) {
					this._resetSubjects$.get(key)!.next(FeatureFlagType.MAP);
				}
			}

			for (const subKey in value) {
				if (subKey) {
					this._updateMap(subKey, value[subKey], currentMap.get(key), key);
				}
			}
		}
	}

	private _addNewMapEntry(k: string, v: FeatureSetting): void {
		this._flagMap$.set(k, new BehaviorSubject<FeatureSetting>(v));
	}

	private _populateDefault(list: Record<string, FeatureSetting>): void {
		let defaultValue: boolean;
		for (const type of Object.values(list)) {
			// use value from localStorage if it exists
			const flag = this.readLocally(`${this._FEATURE_FLAG_LOCAL_STORAGE_PREFIX}${type}`);
			if (typeof flag === 'boolean') {
				this._addNewMapEntry(type, flag);
			} else {
				defaultValue = DEFAULT_FLAG.get(type);
				if (DEFAULT_FLAG.get(type)) {
					this._addNewMapEntry(type, defaultValue);
				}
			}
		}
	}

	// In case of race condition that app renders before data retrieved from firebase
	// We assume the query args are correct and populate the data based on that
	private _attemptToRetrieveFlag(...args: string[]): BehaviorSubject<FeatureSetting> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let val: any = this._flagMap$;
		let previousVal;
		let arg: string;
		for (let i = 0; i < args.length; i++) {
			arg = args[i];
			previousVal = val;
			if (val instanceof BehaviorSubject) {
				return val;
			}
			val = val.get(arg);

			if (val === null || val === undefined) {
				previousVal.set(
					arg,
					i === args.length - 1
						? new BehaviorSubject<boolean>(false)
						: new Map<string, BehaviorSubject<FeatureSetting>>()
				);
				val = previousVal.get(arg);
			}
		}

		return val;
	}

	private _destroyObs(
		flagMap: Map<string, BehaviorSubject<FeatureSetting> | Map<string, FeatureSetting>>
	): void {
		flagMap.forEach((val) => {
			if (val instanceof Map) {
				this._destroyObs(val as Map<string, FeatureSetting>);
			} else {
				val.complete();
			}
		});
	}
}
